import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Upload, Delete, Description, PictureAsPdf, TableChart, Image } from '@mui/icons-material';
import { useRef, useState, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton } from '@/shared/components';

interface DocumentUploadProps {
  documents: File[];
  onChange: (documents: File[]) => void;
  maxDocuments?: number;
  maxSizeMB?: number;
  existingDocuments?: { id: string; filename: string; fileSize: number; url: string }[];
  onDeleteExisting?: (id: string) => void;
}

export const DocumentUpload = ({
  documents,
  onChange,
  maxDocuments = 5,
  maxSizeMB = 20,
  existingDocuments = [],
  onDeleteExisting,
}: DocumentUploadProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>('');

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (file: File) => {
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();

    // PDF files
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
      return <PictureAsPdf sx={{ color: 'white', fontSize: 24 }} />;
    }

    // Excel files
    if (
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx')
    ) {
      return <TableChart sx={{ color: 'white', fontSize: 24 }} />;
    }

    // Image files
    if (
      fileType.startsWith('image/') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png')
    ) {
      return <Image sx={{ color: 'white', fontSize: 24 }} />;
    }

    // Word and other documents (default)
    return <Description sx={{ color: 'white', fontSize: 24 }} />;
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Reset error
    setError('');

    // Validate count
    if (existingDocuments.length + documents.length + files.length > maxDocuments) {
      setError(t('publish.documents.maxDocumentsError', { maxDocuments }));
      return;
    }

    // Validate each file
    const validFiles: File[] = [];
    const allowedTypes = new Set([
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/png',
    ]);

    for (const file of files) {
      // Validate file type
      if (!allowedTypes.has(file.type)) {
        setError(t('publish.documents.invalidFormatError'));
        continue;
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxSizeMB) {
        setError(t('publish.documents.maxSizeError', { maxSizeMB }));
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      onChange([...documents, ...validFiles]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = (index: number) => {
    const newDocuments = documents.filter((_, i) => i !== index);
    onChange(newDocuments);
    setError('');
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Add Document Button - Matching Mockup Design */}
      <AppButton
        variant="outlined"
        fullWidth
        onClick={handleButtonClick}
        disabled={existingDocuments.length + documents.length >= maxDocuments}
        startIcon={<Upload />}
        sx={{ mb: 2 }}
      >
        {t('publish.documents.addButton')}
      </AppButton>

      {error && (
        <Typography variant="body2" color="error" mb={2}>
          {error}
        </Typography>
      )}

      {/* Existing (server-side) documents */}
      {existingDocuments.length > 0 && (
        <Stack spacing={2} mb={documents.length > 0 ? 2 : 0}>
          {existingDocuments.map(doc => (
            <Box
              key={doc.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: '10px',
                border: '2px solid',
                borderColor: 'primary.main',
                backgroundColor: '#F9FAFB',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  backgroundColor: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Description sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {doc.filename}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(doc.fileSize)}
                </Typography>
              </Box>
              {onDeleteExisting && (
                <IconButton
                  size="small"
                  onClick={() => onDeleteExisting(doc.id)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Document list */}
      {documents.length > 0 && (
        <Stack spacing={2}>
          {documents.map((document, index) => (
            <Box
              key={`${document.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: '10px',
                border: '1px solid #E5E7EB',
                backgroundColor: '#F9FAFB',
              }}
            >
              {/* Document icon */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  backgroundColor: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {getFileIcon(document)}
              </Box>

              {/* Document info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {document.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(document.size)}
                </Typography>
              </Box>

              {/* Delete button */}
              <IconButton
                size="small"
                onClick={() => handleDelete(index)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                  },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        {t('publish.documents.helperText')}
      </Typography>
    </Box>
  );
};
